import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Check, MousePointer2 } from "lucide-react";

type Step = {
  id: number;
  scene: "create" | "questions" | "results";
};

const steps: Step[] = [
  { id: 0, scene: "create" },
  { id: 1, scene: "questions" },
  { id: 2, scene: "results" },
];

const HeroDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [cursorPos, setCursorPos] = useState({ x: 200, y: 180 });
  const [clicking, setClicking] = useState(false);
  const [typing, setTyping] = useState("");
  const [showCheck, setShowCheck] = useState(false);

  const scene = steps[currentStep].scene;

  useEffect(() => {
    const sequence = async () => {
      // Scene: Create Exam
      if (scene === "create") {
        setCursorPos({ x: 180, y: 95 });
        await wait(600);
        setClicking(true);
        await wait(200);
        setClicking(false);
        await wait(300);
        // Type exam name
        const name = "Physics Mid-Term";
        for (let i = 0; i <= name.length; i++) {
          setTyping(name.slice(0, i));
          await wait(60);
        }
        await wait(800);
        // Click create button
        setCursorPos({ x: 180, y: 220 });
        await wait(500);
        setClicking(true);
        await wait(200);
        setClicking(false);
        setShowCheck(true);
        await wait(1200);
        setShowCheck(false);
        setTyping("");
        setCurrentStep(1);
      }

      if (scene === "questions") {
        setCursorPos({ x: 60, y: 110 });
        await wait(600);
        setClicking(true);
        await wait(200);
        setClicking(false);
        await wait(400);
        // Select option B
        setCursorPos({ x: 60, y: 155 });
        await wait(500);
        setClicking(true);
        await wait(200);
        setClicking(false);
        await wait(400);
        // Click Next
        setCursorPos({ x: 250, y: 230 });
        await wait(500);
        setClicking(true);
        await wait(200);
        setClicking(false);
        await wait(1000);
        setCurrentStep(2);
      }

      if (scene === "results") {
        setCursorPos({ x: 160, y: 120 });
        await wait(1500);
        setCursorPos({ x: 100, y: 200 });
        await wait(2000);
        // Loop back
        setCurrentStep(0);
      }
    };

    sequence();
  }, [scene]);

  return (
    <div className="relative w-full max-w-sm mx-auto">
      {/* Browser chrome */}
      <div className="bg-foreground rounded-2xl shadow-2xl overflow-hidden border border-foreground/20">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-foreground/95">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 mx-4">
            <div className="bg-foreground/50 rounded-lg px-3 py-1 text-xs text-background/50 text-center font-mono">
              edulabs.app
            </div>
          </div>
        </div>

        {/* Screen content */}
        <div className="bg-card relative overflow-hidden" style={{ height: 280 }}>
          <AnimatePresence mode="wait">
            {scene === "create" && (
              <motion.div
                key="create"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-5"
              >
                <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">Create New Exam</div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs font-semibold text-foreground mb-1">Exam Title</div>
                    <div className="h-9 rounded-lg border bg-background px-3 flex items-center">
                      <span className="text-sm text-foreground">{typing}</span>
                      <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ repeat: Infinity, duration: 0.8 }}
                        className="w-0.5 h-4 bg-foreground ml-0.5"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-xs font-semibold text-foreground mb-1">Class</div>
                      <div className="h-9 rounded-lg border bg-background px-3 flex items-center text-sm text-muted-foreground">12</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-foreground mb-1">Duration</div>
                      <div className="h-9 rounded-lg border bg-background px-3 flex items-center text-sm text-muted-foreground">60 min</div>
                    </div>
                  </div>
                  <motion.div
                    className="h-10 rounded-xl bg-foreground flex items-center justify-center text-background text-sm font-bold mt-2 relative overflow-hidden"
                  >
                    {showCheck ? (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1">
                        <Check className="h-4 w-4" /> Created!
                      </motion.div>
                    ) : (
                      "Create Exam"
                    )}
                  </motion.div>
                </div>
              </motion.div>
            )}

            {scene === "questions" && (
              <motion.div
                key="questions"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Question 1 of 10</div>
                  <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">⏱ 58:42</div>
                </div>
                <div className="text-sm font-semibold text-foreground mb-4">
                  What is the SI unit of force?
                </div>
                <div className="space-y-2">
                  {["Joule", "Newton", "Pascal", "Watt"].map((opt, i) => (
                    <motion.div
                      key={opt}
                      className={`h-9 rounded-xl border px-4 flex items-center text-sm cursor-pointer transition-colors ${
                        i === 1 && currentStep === 1
                          ? "bg-foreground text-background border-foreground font-semibold"
                          : "bg-background text-foreground hover:bg-muted"
                      }`}
                    >
                      <span className="w-5 h-5 rounded-full border flex items-center justify-center mr-3 text-xs font-bold flex-shrink-0">
                        {String.fromCharCode(65 + i)}
                      </span>
                      {opt}
                    </motion.div>
                  ))}
                </div>
                <div className="flex justify-end mt-4">
                  <div className="h-9 rounded-xl bg-foreground px-6 flex items-center text-background text-sm font-bold">
                    Next →
                  </div>
                </div>
              </motion.div>
            )}

            {scene === "results" && (
              <motion.div
                key="results"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="p-5"
              >
                <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Exam Results</div>
                <div className="text-center py-2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                    className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-2"
                  >
                    <span className="text-2xl font-black text-green-600">85%</span>
                  </motion.div>
                  <div className="text-sm font-bold text-foreground">Great Performance!</div>
                  <div className="text-xs text-muted-foreground">Physics Mid-Term • Class 12</div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {[
                    { label: "Correct", value: "17", color: "text-green-600" },
                    { label: "Wrong", value: "3", color: "text-red-500" },
                    { label: "Rank", value: "#4", color: "text-primary" },
                  ].map((stat) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="bg-muted rounded-xl p-3 text-center"
                    >
                      <div className={`text-lg font-black ${stat.color}`}>{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
                {/* Progress bars */}
                <div className="mt-4 space-y-2">
                  {[
                    { label: "Mechanics", pct: 90 },
                    { label: "Optics", pct: 75 },
                    { label: "Thermodynamics", pct: 60 },
                  ].map((topic) => (
                    <div key={topic.label} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-24 text-right">{topic.label}</span>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${topic.pct}%` }}
                          transition={{ duration: 0.8, delay: 0.6 }}
                          className="h-full bg-foreground rounded-full"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Animated cursor */}
          <motion.div
            animate={{ x: cursorPos.x, y: cursorPos.y, scale: clicking ? 0.8 : 1 }}
            transition={{ type: "spring", stiffness: 150, damping: 20 }}
            className="absolute pointer-events-none z-20"
            style={{ top: 0, left: 0 }}
          >
            <MousePointer2
              className="h-5 w-5 drop-shadow-lg"
              fill="hsl(var(--foreground))"
              stroke="hsl(var(--card))"
              strokeWidth={1.5}
            />
            {clicking && (
              <motion.div
                initial={{ scale: 0, opacity: 0.5 }}
                animate={{ scale: 2.5, opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute top-0 left-0 w-4 h-4 rounded-full bg-foreground/30"
              />
            )}
          </motion.div>
        </div>
      </div>

      {/* Scene indicators */}
      <div className="flex justify-center gap-2 mt-4">
        {["Create", "Attempt", "Results"].map((label, i) => (
          <div
            key={label}
            className={`text-xs font-semibold px-3 py-1 rounded-full transition-all ${
              currentStep === i
                ? "bg-foreground text-background"
                : "bg-foreground/10 text-foreground/50"
            }`}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  );
};

function wait(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

export default HeroDemo;
